import { useTranslate } from "@refinedev/core";
import { HandHeart, HeartPulse, Megaphone, PawPrint } from "lucide-react";

import "./OpvangModules.css";

/**
 * The overview switcher above the Opvangoverzichten screens (maintainer 2026-08-18: "ik wil de items in
 * het linker menu verplaatst hebben naar opvangoverzichten").
 *
 * <p>Pleeggezinnen, Nazorg, Uitwisselingen en Advertenties waren allemaal eigen menu-items. Het zijn
 * antwoorden op dezelfde vraag — waar zijn de dieren nu — dus ze staan hier bij elkaar, en het
 * linkermenu houdt één "Opvangoverzichten" over in plaats van vijf.
 *
 * <p>Each segment stays its own page: the URL, the module gate and every deep-link are unchanged, so a
 * bookmark on /pleeggezin-overzicht still lands exactly where it did — it now simply shows this bar on top.
 * Segments whose module is switched off in Modulebeheer are not rendered, the same rule the sidebar used.
 */

/** The pages that carry the switcher, in the order of the bar. */
export const OPVANG_MODULES = [
  { page: "opvang-overzichten", labelKey: "opvangModules.opvang", icon: PawPrint },
  {
    page: "pleeggezin-overzicht",
    labelKey: "opvangModules.pleeggezinnen",
    icon: HandHeart,
    moduleKey: "pleeggezin",
  },
  { page: "nazorg", labelKey: "opvangModules.nazorg", icon: HeartPulse, moduleKey: "nazorg" },
  // Uitwisselingen staat er op verzoek even uit. De pagina blijft bestaan en /uitwisselingen werkt
  // gewoon; alleen de knop in deze rij is weg. Terugzetten = deze regels weer aanzetten.
  // Advertenties hoort in dezelfde rij: ook dat gaat over waar de dieren nu zijn — namelijk op de
  // site, of nog niet. Het stond in het linkermenu als eigen groep met één item.
  {
    page: "advertenties",
    labelKey: "opvangModules.advertenties",
    icon: Megaphone,
    moduleKey: "advertentie",
  },
] as const;

export type OpvangModulePage = (typeof OPVANG_MODULES)[number]["page"];

/** Whether this page is one of the four — App uses it to decide whether to render the bar. */
export function isOpvangModulePage(page: string): page is OpvangModulePage {
  return OPVANG_MODULES.some((m) => m.page === page);
}

export interface OpvangModuleBarProps {
  current: string;
  onSelect: (page: OpvangModulePage) => void;
  /** Same gate as the sidebar had: a module that is off does not appear. */
  isModuleEnabled: (key: string) => boolean;
}

export function OpvangModuleBar({ current, onSelect, isModuleEnabled }: OpvangModuleBarProps) {
  const t = useTranslate();
  const zichtbaar = OPVANG_MODULES.filter(
    (m) => !("moduleKey" in m) || isModuleEnabled(m.moduleKey),
  );

  return (
    <nav className="opvang-modules" aria-label={t("menu.opvangOverzichten")}>
      {zichtbaar.map(({ page, labelKey, icon: Icon }) => (
        <button
          key={page}
          type="button"
          className={page === current ? "opvang-modules__knop is-actief" : "opvang-modules__knop"}
          aria-current={page === current ? "page" : undefined}
          onClick={() => onSelect(page)}
        >
          <Icon size={16} aria-hidden="true" />
          {t(labelKey)}
        </button>
      ))}
    </nav>
  );
}
